<template>
    <div class="funnel-list">
        <div class="relative">
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50 sticky top-0 z-10">
                        <tr>
                            <th scope="col"
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                <input type="checkbox"
                                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    :checked="selectedRowKeys.length === funnels.length" @change="toggleAllRows">
                            </th>
                            <th v-for="column in columns" :key="column.key" scope="col"
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                {{ column.title }}
                            </th>
                        </tr>
                    </thead>
                </table>
            </div>
            <div class="overflow-y-auto" style="max-height: 400px;">
                <table class="min-w-full divide-y divide-gray-200">
                    <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="record in funnels" :key="record.id" class="hover:bg-gray-50"
                            :class="{'bg-blue-50': selectedRowKeys.includes(record.id)}">
                            <td class="px-6 py-2 whitespace-nowrap">
                                <input type="checkbox"
                                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                    :checked="selectedRowKeys.includes(record.id)" @change="(e) => {
                                           e.stopPropagation();
                                           toggleRow(record.id);
                                       }">
                            </td>
                            <td class="px-6 py-2 whitespace-nowrap">
                                <div class="text-sm font-medium text-gray-900">{{ record.name }}</div>
                            </td>
                            <td class="px-6 py-2 whitespace-nowrap">
                                <span :class="[
                                    'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                    record.status === 'active' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-gray-100 text-gray-800'
                                ]">
                                    {{ record.status }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { getFunnelList } from '@/api/funnels'

interface Funnel {
    id: number;
    name: string;
    status: string;
    weight?: number;
}

const columns = [
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
    }
];

const funnels = ref<Funnel[]>([]);
const selectedRowKeys = ref<number[]>([]);
const funnelWeights = ref<Record<number, number>>({});

const toggleRow = (id: number) => {
    const index = selectedRowKeys.value.indexOf(id);
    if (index === -1) {
        selectedRowKeys.value.push(id);
        // 初始化权重为1
        funnelWeights.value[id] = 0;
    } else {
        selectedRowKeys.value.splice(index, 1);
        // 移除权重
        delete funnelWeights.value[id];
    }
};

const toggleAllRows = (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
        selectedRowKeys.value = funnels.value.map(funnel => funnel.id);
        // 初始化所有选中项的权重为1
        funnels.value.forEach(funnel => {
            funnelWeights.value[funnel.id] = 0;
        });
    } else {
        selectedRowKeys.value = [];
        funnelWeights.value = {};
    }
};

const getSelectedFunnels = () => {
    return funnels.value
        .filter(funnel => selectedRowKeys.value.includes(funnel.id))
        .map(funnel => ({
            ...funnel,
            weight: funnelWeights.value[funnel.id] || 0
        }));
};

// 初始化选中的funnel
const initSelectedFunnels = (funnels: Funnel[]) => {
    selectedRowKeys.value = funnels.map(funnel => funnel.id);
    // 初始化权重
    funnels.forEach(funnel => {
        funnelWeights.value[funnel.id] = funnel.weight || 0;
    });
};

// 模拟获取漏斗列表数据
const getFunnels = async () => {
    const res = await getFunnelList()
    funnels.value = res.funnels
};

onMounted(() => {
    getFunnels();
});

defineExpose({
    getSelectedFunnels,
    selectedRowKeys,
    initSelectedFunnels,
});
</script>

<style scoped>
.funnel-list {
    padding: 16px;
}
</style> 